import { SelectInput } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { Spin, Typography } from 'antd';
import React, { useMemo } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { IAsyncService } from '../../../../../../expressions/repository/async/context/IAsyncService';
import { SelectWidgetWrapper } from '../../../common/select-widget/SelectWidgetWrapper';
import { IWidgetParsedProps } from '../../../common/util/getWidgetProps';
import { IAsyncSelectWidget } from '../../IAsyncSelectWidget';
import { getLabelFields } from '../label/getLabelFields';
import { getSelectedValueLabel } from '../label/getSelectedValueLabel';

export interface IAsyncSelectWidgetSelectInputProps {
  widget: IAsyncSelectWidget;
  widgetOptions: IWidgetParsedProps;
  readonly: boolean | undefined;
  data: any[] | null;
  isLoading: boolean;
  error: any;
  objectFieldName: string;
  service: IAsyncService | null;
  setSearchQuery: (value: string | null) => void;
  searchQuery: string | null;
  name: string | undefined;
}

/**
 * Input dinamico quando il widget AsyncSelect è in modalità "select".
 */
export function AsyncSelectWidgetSelectInput(
  props: IAsyncSelectWidgetSelectInputProps,
) {
  const {
    widget,
    widgetOptions,
    readonly,
    data,
    isLoading,
    error,
    objectFieldName,
    service,
    setSearchQuery,
    searchQuery,
  } = props;
  const { t } = useTranslation();

  const { field: objectField } = useController({ name: objectFieldName });

  /**
   * Nascondiamo gli errori per i servizi in autocomplete, mostrando
   * invece un messaggio "Digita per cercare" quando il campo è vuoto.
   */
  const shouldHideErrorBecauseAutocomplete =
    error && !searchQuery && widget.searchField != null;

  const labelFields = useMemo(() => {
    return getLabelFields(widget);
  }, [widget]);

  const watchedValues = useMemo(() => {
    // NB è importante che l'ordine sia rispettato per il calcolo della label + value
    return [...labelFields, widget.valueField].map(
      f => `${objectFieldName}.${f}`,
    );
  }, [objectFieldName, labelFields, widget.valueField]);

  // Campo label e value precedentemente selezionati
  const fieldData = useWatch({
    name: watchedValues,
  });

  // Costruisco le opzioni della select
  const { hasSimulatedOptions, options } = useMemo(() => {
    if (Boolean(error) || !data || readonly) {
      // Ricostruisco l'oggetto con i valori selezionati, necessari per la label
      const values = labelFields.reduce((acc, fieldName, index) => {
        acc[fieldName] = fieldData[index];
        return acc;
      }, {} as Record<string, any>);

      // Ritorno comunque il valore selezionato.
      return {
        options: [
          {
            label: getSelectedValueLabel(widget, values),
            // Il value è l'ultimo elemento dell'array dei field data ritornati dallo useWatch
            value: fieldData.slice(-1)[0],
          },
        ],
        hasSimulatedOptions: true,
      };
    }

    return {
      hasSimulatedOptions: false,
      options: data.map(d => ({
        label: getSelectedValueLabel(widget, d),
        // I valori sono sempre di tipo stringa, in quanto il Widget stesso
        // definisce lo schema come `yup.string()`. Se è necessario il valore
        // numerico, è possibile usare la Reference interna all'objectField
        value: d[widget.valueField]?.toString(),
        objectValue: d,
      })),
    };
  }, [error, data, readonly, labelFields, widget, fieldData]);

  return (
    <SelectWidgetWrapper {...props}>
      <SelectInput
        readOnly={props.readonly}
        name={widgetOptions.name}
        placeholder={widgetOptions.placeholder}
        allowClear
        showSearch
        filterOption
        optionFilterProp="label"
        {...(widget.searchField && {
          onSearch: (value: string) => {
            if (!widget.searchField) return;

            if (value === '') {
              setSearchQuery(null);
              return;
            }
            setSearchQuery(value);
          },
        })}
        onChange={(value, option, onChange) => {
          onChange(value);
          objectField.onChange((option as any)?.objectValue);
          objectField.onBlur();
        }}
        notFoundContent={
          isLoading ? (
            <Spin size="small" />
          ) : shouldHideErrorBecauseAutocomplete ? (
            t(
              'widgets.async-select-widget-component.type-to-search',
              'Digita per iniziare a cercare',
            )
          ) : (
            t(
              'widgets.async-select-widget-component.not-found',
              'Nessun elemento trovato.',
            )
          )
        }
        loading={isLoading}
        // Non resettiamo mai il campo se non c'è un valore selezionato.
        // Questo per evitare che il campo venga resettato in caso di errore,
        // servizi che ritornano liste vuote, ecc.
        skipResetOnMissingValue={true}
        options={options}
      />

      {error && !shouldHideErrorBecauseAutocomplete && (
        <Typography.Text type="danger">
          {t(
            'widgets.async-select-widget-component.service-not-responding',
            'Impossibile recuperare i dati dal servizio {{service}}. Riprovare più tardi.',
            { service: service?.name ? `"${service.name}"` : '' },
          )}
        </Typography.Text>
      )}
    </SelectWidgetWrapper>
  );
}
